import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, BookOpen, Layers, Library, Lock } from "lucide-react";
import { useProgress } from "@/lib/useProgress";
import ThemeToggle from "@/components/hormone/ThemeToggle";
import DiagramRenderer from "@/components/hormone/DiagramRenderer";
import Flashcards from "@/components/hormone/Flashcards";
import ShareButton from "@/components/hormone/ShareButton";
import ResourcesView from "@/components/hormone/ResourcesView";
import AiActions from "@/components/hormone/AiActions";
import { useToast } from "@/hooks/use-toast";
import {
  COURSES,
  getCourseBySlug,
  findLessonLocation,
  courseProgress,
} from "@/data/courses";

// -------------------------------------------------------------------------
// Shared chrome (header)
// -------------------------------------------------------------------------
function AppHeader({ left, center, extra }) {
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        background: "var(--scrim)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: "8px 16px",
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          alignItems: "center",
          gap: 16,
          minHeight: 56,
        }}
      >
        <div>{left}</div>
        <div
          style={{
            textAlign: "center",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--muted)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {center}
        </div>
        <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
          {extra}
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}

function BackButton({ onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn btn-ghost touch"
      data-testid={testid}
      aria-label={`Back to ${label}`}
      style={{ padding: "0 14px", minHeight: 40, fontSize: 14 }}
    >
      <ArrowLeft size={16} strokeWidth={1.75} aria-hidden="true" />
      <span
        style={{
          maxWidth: 140,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    </button>
  );
}

// -------------------------------------------------------------------------
// Skeleton screen (used on first paint of the hub)
// -------------------------------------------------------------------------
function HomeSkeleton() {
  return (
    <div
      data-testid="home-skeleton"
      style={{ padding: 16, maxWidth: 760, margin: "0 auto" }}
    >
      <div className="skeleton" style={{ height: 32, width: "70%", marginBottom: 16 }} />
      <div className="skeleton" style={{ height: 16, width: "45%", marginBottom: 24 }} />
      <div className="skeleton" style={{ height: 8, width: "100%", marginBottom: 32 }} />
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="skeleton"
          style={{ height: 140, marginBottom: 16 }}
        />
      ))}
    </div>
  );
}

// -------------------------------------------------------------------------
// Course Hub – top-level selector (the new home)
// -------------------------------------------------------------------------
function CourseHub({ onOpenCourse, onOpenLibrary }) {
  const { completed } = useProgress();

  return (
    <>
      <AppHeader
        left={
          <div
            className="t-meta"
            style={{ color: "var(--accent)" }}
            data-testid="brand-label"
          >
            Hikari Labs
          </div>
        }
        center="The learning hub"
        extra={
          <button
            type="button"
            onClick={onOpenLibrary}
            className="btn btn-ghost touch"
            data-testid="open-library-btn"
            aria-label="Open library of research and resources"
            style={{ padding: "0 12px", minHeight: 44, fontSize: 13, gap: 6 }}
          >
            <Library size={16} strokeWidth={1.75} aria-hidden="true" />
            Library
          </button>
        }
      />

      <main
        style={{ maxWidth: 760, margin: "0 auto", padding: "24px 16px 64px" }}
      >
        <section className="rise" style={{ marginBottom: 32 }}>
          <h1
            className="t-display"
            style={{ margin: "0 0 8px", color: "var(--text)" }}
            data-testid="hub-title"
          >
            Choose your path
          </h1>
          <p
            className="t-body"
            style={{ margin: "0 0 8px", maxWidth: 560 }}
            data-testid="hub-subtitle"
          >
            Two courses, one underlying story — the human ecosystem and how it
            shapes everything from hormones to mood.
          </p>
        </section>

        <nav aria-label="Available courses" data-testid="hub-courses">
          {COURSES.map(({ slug, course, accent, tagline, hubBlurb }, i) => {
            const { done, total, percent } = courseProgress(course, completed);
            const allModules = course.modules.length;
            const liveModules = course.modules.filter(
              (m) => !m.comingSoon
            ).length;
            return (
              <button
                key={slug}
                type="button"
                onClick={() => onOpenCourse(slug)}
                data-testid={`hub-course-${slug}`}
                className="card card-hover rise touch"
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: 22,
                  marginBottom: 18,
                  borderLeft: `4px solid var(${accent})`,
                  display: "block",
                  animationDelay: `${i * 80}ms`,
                }}
              >
                <div className="t-meta" style={{ marginBottom: 6 }}>
                  {tagline}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 22,
                    fontWeight: 500,
                    color: `var(${accent})`,
                    letterSpacing: "-0.01em",
                    marginBottom: 8,
                    lineHeight: 1.2,
                  }}
                >
                  {course.title}
                </div>
                <p
                  className="t-body"
                  style={{
                    fontSize: 14,
                    margin: "0 0 16px",
                    lineHeight: 1.6,
                  }}
                >
                  {hubBlurb}
                </p>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    fontSize: 12,
                    fontFamily: "var(--font-mono)",
                    color: "var(--muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: 12,
                  }}
                >
                  <span>
                    {liveModules}/{allModules} modules live
                  </span>
                  <span style={{ opacity: 0.5 }}>·</span>
                  <span>
                    {done}/{total} lessons · {percent}%
                  </span>
                </div>

                <div
                  style={{
                    height: 6,
                    borderRadius: 999,
                    background: "var(--surface-strong)",
                    overflow: "hidden",
                  }}
                  role="progressbar"
                  aria-valuenow={percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${percent}%`,
                      background: `var(${accent})`,
                      transition: "width 400ms var(--ease)",
                    }}
                  />
                </div>
              </button>
            );
          })}
        </nav>
      </main>
    </>
  );
}

// -------------------------------------------------------------------------
// Course Home – module list for a single course
// -------------------------------------------------------------------------
function CourseHome({
  course,
  accent,
  tagline,
  onOpenModule,
  onOpenLibrary,
  onBackToHub,
  progress,
  completedCount,
  totalLessons,
}) {
  const sourceCopy =
    course.sourceNote ||
    "Based on peer-reviewed research from PubMed, Frontiers in Microbiology, Journal of Biological Chemistry, and clinical functional medicine literature.";

  return (
    <>
      <AppHeader
        left={
          <BackButton
            onClick={onBackToHub}
            label="Hub"
            testid="course-hub-back-btn"
          />
        }
        center={tagline}
        extra={
          <button
            type="button"
            onClick={onOpenLibrary}
            className="btn btn-ghost touch"
            data-testid="open-library-btn"
            aria-label="Open library of research and resources"
            style={{ padding: "0 12px", minHeight: 44, fontSize: 13, gap: 6 }}
          >
            <Library size={16} strokeWidth={1.75} aria-hidden="true" />
            Library
          </button>
        }
      />

      <main style={{ maxWidth: 760, margin: "0 auto", padding: "24px 16px 64px" }}>
        {/* Hero */}
        <section className="rise" style={{ marginBottom: 32 }}>
          <h1
            className="t-display"
            data-testid="course-title"
            style={{ margin: "0 0 8px", color: "var(--text)" }}
          >
            {course.title}
          </h1>
          <p
            className="t-body"
            style={{ margin: "0 0 24px", maxWidth: 560 }}
            data-testid="course-subtitle"
          >
            {course.subtitle}
          </p>

          {/* Progress */}
          <div
            role="group"
            aria-label="Course progress"
            style={{ marginBottom: 8 }}
          >
            <div
              style={{
                height: 8,
                borderRadius: 999,
                background: "var(--surface-strong)",
                overflow: "hidden",
              }}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              data-testid="course-progress-bar"
            >
              <div
                style={{
                  height: "100%",
                  width: `${progress}%`,
                  background: `linear-gradient(90deg, var(${accent}), var(--mod-2))`,
                  transition: "width 400ms var(--ease)",
                }}
              />
            </div>
          </div>
          <div
            className="t-meta"
            data-testid="course-progress-label"
            style={{ color: "var(--muted)" }}
          >
            {completedCount} of {totalLessons} lessons · {progress}%
          </div>
        </section>

        {/* Source note */}
        <aside
          className="card rise"
          style={{
            padding: "14px 18px",
            marginBottom: 24,
            background: `color-mix(in srgb, var(${accent}) 5%, var(--surface))`,
            borderColor: `color-mix(in srgb, var(${accent}) 20%, var(--border))`,
          }}
          data-testid="source-note"
        >
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: "var(--text-2)",
              lineHeight: 1.6,
            }}
          >
            {sourceCopy}
          </p>
        </aside>

        {/* Modules list */}
        <nav aria-label="Course modules" data-testid="modules-list">
          {course.modules.map((mod, i) => (
            <ModuleCard
              key={mod.id}
              index={i}
              mod={mod}
              onOpen={mod.comingSoon ? null : () => onOpenModule(i)}
            />
          ))}
        </nav>
      </main>
    </>
  );
}

function ModuleCard({ mod, onOpen, index }) {
  const { isDone } = useProgress();
  const total = mod.lessons.length;
  const done = mod.lessons.filter((l) => isDone(l.id)).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isLocked = !onOpen;
  const flashCount = mod.lessons.reduce(
    (s, l) => s + (l.content?.flashcards?.length || 0),
    0
  );

  return (
    <button
      type="button"
      onClick={onOpen || undefined}
      disabled={isLocked}
      data-testid={`module-card-${mod.id}`}
      className={isLocked ? "card rise" : "card card-hover rise touch"}
      style={{
        width: "100%",
        textAlign: "left",
        padding: 20,
        marginBottom: 16,
        borderLeft: `4px solid var(${mod.colorVar})`,
        display: "block",
        animationDelay: `${index * 60}ms`,
        opacity: isLocked ? 0.62 : 1,
        cursor: isLocked ? "default" : "pointer",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="t-meta" style={{ marginBottom: 4 }}>
            Module {mod.id}
          </div>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 20,
              fontWeight: 500,
              color: `var(${mod.colorVar})`,
              letterSpacing: "-0.01em",
              marginBottom: 4,
              lineHeight: 1.2,
            }}
          >
            <span aria-hidden="true" style={{ marginRight: 8 }}>
              {mod.icon}
            </span>
            {mod.title}
          </div>
          {mod.summary ? (
            <div
              className="t-body"
              style={{ fontSize: 14, margin: "0 0 4px", lineHeight: 1.55 }}
            >
              {mod.summary}
            </div>
          ) : null}
          <div className="t-body" style={{ fontSize: 13, margin: 0 }}>
            {isLocked
              ? "Coming soon"
              : `${total} lesson${total === 1 ? "" : "s"} · ${flashCount} flashcards`}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          {isLocked ? (
            <div
              aria-hidden="true"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "var(--surface-strong)",
                color: "var(--muted)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Lock size={16} strokeWidth={1.75} />
            </div>
          ) : (
            <>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: done === total ? "var(--mod-2)" : "var(--text)",
                }}
                data-testid={`module-count-${mod.id}`}
              >
                {done}/{total}
              </div>
              <div className="t-meta" style={{ marginTop: 2 }}>
                complete
              </div>
            </>
          )}
        </div>
      </div>
      {!isLocked && (
        <div
          style={{
            marginTop: 16,
            height: 4,
            borderRadius: 999,
            background: "var(--surface-strong)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: `var(${mod.colorVar})`,
              transition: "width 400ms var(--ease)",
            }}
          />
        </div>
      )}
    </button>
  );
}

// -------------------------------------------------------------------------
// Module view – lessons list (course-aware)
// -------------------------------------------------------------------------
function ModuleView({ course, modIndex, onBack, onOpenLesson }) {
  const { isDone } = useProgress();
  const mod = course.modules[modIndex];
  return (
    <>
      <AppHeader
        left={<BackButton onClick={onBack} label="Course" testid="module-back-btn" />}
        center={`Module ${mod.id} · ${mod.title}`}
      />
      <main style={{ maxWidth: 760, margin: "0 auto", padding: "24px 16px 64px" }}>
        <div className="rise" style={{ marginBottom: 24 }}>
          <div className="t-meta" style={{ marginBottom: 8 }}>
            Module {mod.id}
          </div>
          <h1
            className="t-display"
            style={{ margin: "0 0 8px", color: `var(${mod.colorVar})` }}
            data-testid={`module-title-${mod.id}`}
          >
            <span aria-hidden="true" style={{ marginRight: 10 }}>
              {mod.icon}
            </span>
            {mod.title}
          </h1>
          {mod.summary && (
            <p
              className="t-body"
              style={{ margin: "0 0 8px", maxWidth: 560 }}
            >
              {mod.summary}
            </p>
          )}
        </div>

        <nav aria-label={`Lessons in module ${mod.id}`}>
          {mod.lessons.map((lesson, i) => {
            const done = isDone(lesson.id);
            return (
              <button
                key={lesson.id}
                type="button"
                onClick={() => onOpenLesson(i)}
                data-testid={`lesson-card-${lesson.id}`}
                className="card card-hover rise touch"
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: 16,
                  marginBottom: 12,
                  borderLeft: `3px solid ${
                    done ? "var(--mod-2)" : `var(${mod.colorVar})`
                  }`,
                  animationDelay: `${i * 60}ms`,
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: done
                      ? "color-mix(in srgb, var(--mod-2) 18%, transparent)"
                      : "var(--surface-strong)",
                    color: done ? "var(--mod-2)" : "var(--text-2)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {done ? (
                    <Check size={18} strokeWidth={2.5} />
                  ) : (
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>
                      {lesson.id}
                    </span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: "var(--text)",
                      marginBottom: 2,
                    }}
                  >
                    {lesson.title}
                  </div>
                  <div
                    className="t-body"
                    style={{ fontSize: 13, margin: 0 }}
                  >
                    {lesson.duration} · {lesson.content.flashcards.length} flashcards
                  </div>
                </div>
              </button>
            );
          })}
        </nav>
      </main>
    </>
  );
}

// -------------------------------------------------------------------------
// Lesson view (course-aware)
// -------------------------------------------------------------------------
function LessonView({ course, modIndex, lessonIndex, onBack }) {
  const mod = course.modules[modIndex];
  const lesson = mod.lessons[lessonIndex];
  const [tab, setTab] = useState("content");
  const { isDone, markDone, toggle } = useProgress();
  const done = isDone(lesson.id);
  const { toast } = useToast();

  useEffect(() => {
    setTab("content");
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [lesson.id]);

  const onComplete = () => {
    if (!done) {
      markDone(lesson.id);
      toast({
        title: "Lesson complete",
        description: `${lesson.title} · nice work.`,
      });
    } else {
      toggle(lesson.id);
    }
  };

  return (
    <>
      <AppHeader
        left={
          <BackButton
            onClick={onBack}
            label={mod.title}
            testid="lesson-back-btn"
          />
        }
        center={`${lesson.duration} · ${lesson.title}`}
      />

      {/* Tab bar */}
      <div
        role="tablist"
        aria-label="Lesson sections"
        style={{
          position: "sticky",
          top: 56,
          zIndex: 10,
          background: "var(--scrim)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div
          style={{
            maxWidth: 760,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          {[
            { id: "content", label: "Lesson", Icon: BookOpen },
            { id: "flashcards", label: "Flashcards", Icon: Layers },
          ].map(({ id, label, Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(id)}
                data-testid={`tab-${id}`}
                className="touch"
                style={{
                  minHeight: 48,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  color: active ? `var(${mod.colorVar})` : "var(--muted)",
                  borderBottom: active
                    ? `2px solid var(${mod.colorVar})`
                    : "2px solid transparent",
                  transition:
                    "color var(--dur) var(--ease), border-color var(--dur) var(--ease)",
                  fontFamily: "var(--font-body)",
                }}
              >
                <Icon size={16} strokeWidth={1.75} aria-hidden="true" />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <main
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: "24px 16px 80px",
        }}
        data-testid={`lesson-view-${lesson.id}`}
      >
        {tab === "content" ? (
          <article className="rise">
            <h1
              className="t-display"
              data-testid="lesson-title"
              style={{
                margin: "0 0 8px",
                color: `var(${mod.colorVar})`,
                fontSize: 28,
              }}
            >
              {lesson.title}
            </h1>

            <AiActions lesson={lesson} moduleTitle={mod.title} />

            <aside
              className="card"
              style={{
                padding: "14px 18px",
                margin: "16px 0 32px",
                borderLeft: `3px solid var(${mod.colorVar})`,
                background: `color-mix(in srgb, var(${mod.colorVar}) 6%, var(--surface))`,
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 15,
                  lineHeight: 1.65,
                  color: "var(--text-2)",
                }}
              >
                {lesson.content.intro}
              </p>
            </aside>

            {lesson.content.heroImage && (
              <DiagramRenderer
                diagram={{
                  type: "illustration",
                  src: lesson.content.heroImage.src,
                  alt: lesson.content.heroImage.alt,
                  caption: lesson.content.heroImage.caption,
                  credit: lesson.content.heroImage.credit,
                }}
              />
            )}

            {lesson.content.sections.map((sec, i) => (
              <section key={i} style={{ marginBottom: 32 }}>
                <h2
                  className="t-heading"
                  style={{
                    margin: "0 0 12px",
                    color: `var(${mod.colorVar})`,
                  }}
                >
                  {sec.heading}
                </h2>
                {sec.body && (
                  <p
                    style={{
                      margin: 0,
                      fontSize: 16,
                      color: "var(--text-2)",
                      lineHeight: 1.7,
                    }}
                  >
                    {sec.body}
                  </p>
                )}
                {sec.diagram && <DiagramRenderer diagram={sec.diagram} />}
              </section>
            ))}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 12,
                marginTop: 24,
              }}
            >
              <button
                type="button"
                onClick={onComplete}
                data-testid="complete-lesson-btn"
                className={`btn ${done ? "btn-success" : "btn-primary"}`}
                style={{ width: "100%", minHeight: 48 }}
                aria-pressed={done}
              >
                {done ? (
                  <>
                    <Check size={18} strokeWidth={2} aria-hidden="true" />
                    Completed — tap to undo
                  </>
                ) : (
                  <>Mark lesson as complete</>
                )}
              </button>
              <ShareButton
                lessonId={lesson.id}
                title={lesson.title}
                summary={lesson.content.intro}
              />
            </div>
          </article>
        ) : (
          <Flashcards cards={lesson.content.flashcards} lessonId={lesson.id} />
        )}
      </main>
    </>
  );
}

// -------------------------------------------------------------------------
// Root orchestrator
// -------------------------------------------------------------------------
//
// View state shape:
//   { courseSlug: null | "hormone" | "microbiome",
//     module:     null | number,
//     lesson:     null | number,
//     library:    bool }
//
// courseSlug=null + library=false → Hub (the new home)
// courseSlug=null + library=true  → Library accessed from hub
// courseSlug set, others null     → that course's home
// courseSlug + module             → module's lessons list
// courseSlug + module + lesson    → lesson view

export default function HormoneCourse() {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({
    courseSlug: null,
    module: null,
    lesson: null,
    library: false,
  });
  const { completed } = useProgress();

  // First-paint skeleton (respect reduced motion)
  useEffect(() => {
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const t = setTimeout(() => setLoading(false), reduced ? 0 : 320);
    return () => clearTimeout(t);
  }, []);

  // Deep-link: open lesson by hash, resolving which course owns it
  useEffect(() => {
    const apply = () => {
      const m = window.location.hash.match(/^#\/lesson\/([^/?&]+)/);
      if (!m) return;
      const loc = findLessonLocation(m[1]);
      if (loc) {
        setView({
          courseSlug: loc.courseSlug,
          module: loc.moduleIndex,
          lesson: loc.lessonIndex,
          library: false,
        });
      } else {
        window.history.replaceState(null, "", window.location.pathname);
      }
    };
    apply();
    window.addEventListener("hashchange", apply);
    return () => window.removeEventListener("hashchange", apply);
  }, []);

  const activeCourseCfg = view.courseSlug ? getCourseBySlug(view.courseSlug) : null;
  const activeCourse = activeCourseCfg?.course;

  const { totalLessons, completedCount, progress } = useMemo(() => {
    if (!activeCourse)
      return { totalLessons: 0, completedCount: 0, progress: 0 };
    const stats = courseProgress(activeCourse, completed);
    return {
      totalLessons: stats.total,
      completedCount: stats.done,
      progress: stats.percent,
    };
  }, [activeCourse, completed]);

  if (loading) {
    return (
      <>
        <AppHeader
          left={<div className="t-meta">Hikari Labs</div>}
          center="The learning hub"
        />
        <HomeSkeleton />
      </>
    );
  }

  // ---- Library ----------------------------------------------------------
  if (view.library) {
    return (
      <ResourcesView
        onBack={() =>
          setView((v) => ({
            ...v,
            library: false,
          }))
        }
      />
    );
  }

  // ---- Hub --------------------------------------------------------------
  if (!view.courseSlug) {
    return (
      <CourseHub
        onOpenCourse={(slug) =>
          setView({
            courseSlug: slug,
            module: null,
            lesson: null,
            library: false,
          })
        }
        onOpenLibrary={() =>
          setView((v) => ({ ...v, library: true }))
        }
      />
    );
  }

  // ---- Inside a course --------------------------------------------------
  if (!activeCourse) {
    // Bad slug — fall back to hub
    setView({ courseSlug: null, module: null, lesson: null, library: false });
    return null;
  }

  if (view.module !== null && view.lesson !== null) {
    return (
      <LessonView
        course={activeCourse}
        modIndex={view.module}
        lessonIndex={view.lesson}
        onBack={() => setView((v) => ({ ...v, lesson: null }))}
      />
    );
  }

  if (view.module !== null) {
    return (
      <ModuleView
        course={activeCourse}
        modIndex={view.module}
        onBack={() =>
          setView({
            courseSlug: view.courseSlug,
            module: null,
            lesson: null,
            library: false,
          })
        }
        onOpenLesson={(li) => setView((v) => ({ ...v, lesson: li }))}
      />
    );
  }

  return (
    <CourseHome
      course={activeCourse}
      accent={activeCourseCfg.accent}
      tagline={activeCourseCfg.tagline}
      onOpenModule={(i) =>
        setView((v) => ({ ...v, module: i, lesson: null, library: false }))
      }
      onOpenLibrary={() => setView((v) => ({ ...v, library: true }))}
      onBackToHub={() =>
        setView({
          courseSlug: null,
          module: null,
          lesson: null,
          library: false,
        })
      }
      progress={progress}
      completedCount={completedCount}
      totalLessons={totalLessons}
    />
  );
}
